import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
// Redux
import { useDispatch, useSelector } from "react-redux";
import { updateDistributorDetails } from "../../../../redux/distributorDucks";
// Constants
import { WEB_SERVICE_DASHBOARD_ROUTE } from "../../../../config/constants";
// Components
import {
  Overlay,
  Modal,
  ModalHeader,
  ModalTitle,
  CloseButton,
  CloseIcon,
  Description,
  Field,
  Label,
  Input,
  ErrorMessage,
  Buttons,
  BackButton,
  SaveButton,
} from "./styles";
import Axios from "../../../../config/axios";
import useOverlayClose from "../../../../config/useOverlayClose";
// Assets
import { cross } from "../../../../assets/icons";

const fields = ["company", "email", "phone", "website"];

const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const AccountDetails = ({ onClose }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const name = useSelector(({ distributor }) => distributor.name);
  const savedDetails = useSelector(({ distributor }) => distributor.details);

  // La primera vez el nombre de la empresa se propone a partir del nombre del
  // distribuidor; el resto de campos empiezan vacios.
  const [details, setDetails] = useState({ ...savedDetails, company: savedDetails.company || name });
  const [hasEmailError, setHasEmailError] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const overlayHandlers = useOverlayClose(onClose);

  const changeField = (field, value) => {
    setDetails((prev) => ({ ...prev, [field]: value }));
    if (field === "email") setHasEmailError(false);
  };

  const saveDetails = () => {
    // Ningun campo es obligatorio, pero un email escrito a medias acabaria
    // impreso en el presupuesto que ve el cliente.
    if (details.email.trim() && !isValidEmail(details.email.trim())) return setHasEmailError(true);

    setIsSaving(true);
    Axios("put", `${WEB_SERVICE_DASHBOARD_ROUTE}/distributor/update-details`, {
      COMPANY: details.company,
      EMAIL: details.email,
      PHONE: details.phone,
      WEBSITE: details.website,
    })
      .then(({ data }) => {
        if (!data.detailsUpdated) return toast.error(t("account.saveError"));

        dispatch(updateDistributorDetails(data.details));
        toast.success(t("account.saved"));
        onClose();
      })
      .catch((error) => {
        console.log("Update distributor details: ", error);
        toast.error(t("account.saveError"));
      })
      .finally(() => setIsSaving(false));
  };

  return (
    <Overlay {...overlayHandlers}>
      <Modal>
        <ModalHeader>
          <ModalTitle>{t("account.title")}</ModalTitle>
          <CloseButton type="button" onClick={onClose} aria-label={t("budget.close")}>
            <CloseIcon src={cross} alt="" />
          </CloseButton>
        </ModalHeader>
        <Description>{t("account.description")}</Description>

        {fields.map((field) => (
          <Field key={field}>
            <Label htmlFor={field}>{t(`account.${field}`)}</Label>
            <Input
              id={field}
              type="text"
              placeholder={t(`account.${field}Placeholder`)}
              value={details[field]}
              $hasError={field === "email" && hasEmailError}
              onChange={(e) => changeField(field, e.target.value)}
            />
            {field === "email" && hasEmailError && <ErrorMessage>{t("account.invalidEmail")}</ErrorMessage>}
          </Field>
        ))}

        <Buttons>
          <BackButton type="button" onClick={onClose}>
            {t("account.back")}
          </BackButton>
          <SaveButton type="button" onClick={saveDetails} disabled={isSaving}>
            {isSaving ? t("account.saving") : t("account.save")}
          </SaveButton>
        </Buttons>
      </Modal>
    </Overlay>
  );
};

export default AccountDetails;
